import {BaseStore, globalStore} from "../../../stem-core/state/Store.js";
import {field} from "../../../stem-core/state/StoreField.js";
import {apiClient, type MerchantConversionRequest, type MerchantScopedRequest} from "../../connection/BlinkApiClient.js";
import {BaseEnum, makeEnum} from "../../../stem-core/state/BaseEnum.js";
import {AuthService, authService} from "../../connection/services/AuthService.js";
import {Merchant} from "../MerchantStore";
import {MerchantAudience} from "./MerchantAudienceStore";
import {UserProfile} from "../UserProfileStore";
import {StemDate} from "../../../stem-core/time/Date";
import {MerchantConversion} from "./MerchantConversionStore";
import type {StateLoadOptions, StoreId} from "../../../stem-core/state/State";


@makeEnum
export class MerchantAudienceMemberStatus extends BaseEnum<{value: string}> {
    static SELF_SUBSCRIBED;
    static MANUALLY_SUBSCRIBED;
    static AUTO_SUBSCRIBED;
    static SELF_UNSUBSCRIBED;
    static MANUALLY_UNSUBSCRIBED;
    static AUTO_UNSUBSCRIBED;
    static RULE_SUBSCRIBED;
}

export const ProperSubscribedStatuses = [MerchantAudienceMemberStatus.SELF_SUBSCRIBED, MerchantAudienceMemberStatus.MANUALLY_SUBSCRIBED, MerchantAudienceMemberStatus.AUTO_SUBSCRIBED];
export const SubscribedStatuses = [...ProperSubscribedStatuses, MerchantAudienceMemberStatus.RULE_SUBSCRIBED];
export const ProperUnsubscribedStatuses = [MerchantAudienceMemberStatus.SELF_UNSUBSCRIBED, MerchantAudienceMemberStatus.MANUALLY_UNSUBSCRIBED, MerchantAudienceMemberStatus.AUTO_UNSUBSCRIBED];
export const UnsubscribedStatuses = ProperUnsubscribedStatuses;
export const ProperStatuses = [...ProperSubscribedStatuses, ...ProperUnsubscribedStatuses];

@globalStore
export class MerchantAudienceMember extends BaseStore("MerchantAudienceMember") {
    // Not from the API: MerchantAudienceInstancePage marks the placeholder it builds for a
    // merchant user with no audience-member row
    declare isFake?: boolean;

    @field("Merchant") merchant: Merchant;
    @field(Number) audienceId: number;
    @field("MerchantAudience") audience: MerchantAudience;
    @field("UserProfile") user: UserProfile;
    @field(MerchantAudienceMemberStatus) status: MerchantAudienceMemberStatus;
    @field(Date) unsubscribedAt: StemDate;
    @field(Date) subscribedAt: StemDate;
    @field(Date) createdAt: StemDate;
    @field(Number) conversionId: number;
    @field("MerchantConversion") conversion: MerchantConversion;

    isSubscribed() {
        return SubscribedStatuses.indexOf(this.status) >= 0;
    }

    serializeForSDK() {
        const {audienceId, status, subscribedAt, unsubscribedAt, conversionId} = this;
        return {
            status: status.valueOf(),
            audienceId,
            subscribedAt,
            unsubscribedAt,
            conversionId,
            name: this.audience?.name,
        }
    }
}

// What UserGetNewsletterSubscriptionsRequest validates (users/deserializers.py:195). The edit key is how
// an unauthenticated reader is identified, which is why the endpoint allows any permission
export interface GetNewsletterSubscriptionsRequest {
    editKey: string;
    emailMessageInstanceKey?: string;
}

export async function apiGetNewsletterSubscriptions(request: GetNewsletterSubscriptionsRequest, noAuthToken=false) {
    // The state rides along as usual. The one extra key is the audience of the campaign the tracking key
    // named, left null unless that audience is public (users/views.py:326)
    return apiClient.get<StateLoadOptions & {emailMessageInstanceAudienceId: StoreId | null}>("/users/get_newsletter_subscriptions", request, {noAuthToken});
}

export const NewsletterSubscriptionUpdateType = {
    SUBSCRIBE: "subscribe",
    UNSUBSCRIBE: "unsubscribe",
    REPLACE: "replace",
} as const;

// The three the backend accepts, declared there as a Literal rather than an enum
export type NewsletterSubscriptionUpdate = typeof NewsletterSubscriptionUpdateType[keyof typeof NewsletterSubscriptionUpdateType];

// What UserUpdateNewsletterSubscriptionsRequest validates (users/deserializers.py:200). Either the edit
// key or an authenticated user identifies the reader; the handler ignores emailAddress for the latter
export interface UpdateNewsletterSubscriptionsRequest extends MerchantConversionRequest {
    newsletterIds: StoreId[];
    emailAddress?: string;
    key?: string;
    updateType?: NewsletterSubscriptionUpdate;
    canReceivePromotionalEmails?: boolean;
}

// TODO @branch make this subscribe the user to a non-public newsletter
export async function apiUserUpdateNewsletterSubscriptions(request: UpdateNewsletterSubscriptionsRequest, noAuthToken=false) {
    const response = await apiClient.post("/users/update_newsletter_subscriptions", request, {noAuthToken});
    // We may have been automatically logged in here.
    if (authService.hasAuthToken(response)) {
        // We've been auto-authenticated via registration, since this merchant doesn't care about confirmation codes on new visits
        authService.setAuthToken(response);
        authService.dispatch(AuthService.EventTypes.USER_DATA_AVAILABLE, response);
    }
    return response;
}

// What ImportAudienceMembersRequest validates (dashboard/deserializers.py:60). The backend caps members
// at 100 per call and rejects a status it considers automatic
export interface EditAudienceMembersRequest extends MerchantScopedRequest {
    audienceId: StoreId;
    status: MerchantAudienceMemberStatus;
    overwriteExistingMembers: boolean;
    members: {email: string; firstName?: string; lastName?: string}[];
    createNewUsers?: boolean;
    includeState?: boolean;
}

export async function apiMerchantCreateAudienceMembers(request: EditAudienceMembersRequest) {
    return apiMerchantEditAudienceMembers(request);
}

export async function apiMerchantEditAudienceMembers(request: EditAudienceMembersRequest) {
    return apiClient.post("/merchant/edit_audience_members", request);
}
